'use client'

import React from 'react'
import { Card } from 'components/ui/card'
import { Button } from 'ui/button'
import { Label } from 'ui/label'
import DatePicker from 'components/datetime/DatePicker'
import { FaPlus } from 'react-icons/fa6'
import { LiaTimesSolid } from 'react-icons/lia'
import { CalendarX } from 'lucide-react'
import { addDays, format } from 'date-fns'
import { toast } from 'react-toastify'

export interface DateRange {
    start: string
    end: string
}

interface DateRangeArrayPickerProps {
    fieldName: string
    label: string
    dateRangeArray: DateRange[]
    handleChange: (value: DateRange[]) => void
}

const DateRangeArrayPicker: React.FC<DateRangeArrayPickerProps> = ({
    fieldName,
    label,
    dateRangeArray,
    handleChange
}) => {
    const validateDateRange = (dateRange: DateRange): string | null => {
        if (!dateRange.start) return 'Start date must not be empty'
        if (!dateRange.end) return 'End date must not be empty'
        if (dateRange.end < dateRange.start) return 'End date cannot be before the start date'
        return null
    }

    const addDateRange = () => {
        const today = new Date()
        const tomorrow = addDays(today, 1)
        const oneWeekLater = addDays(tomorrow, 7)

        const newDateRange: DateRange = {
            start: format(tomorrow, 'yyyy-MM-dd'),
            end: format(oneWeekLater, 'yyyy-MM-dd')
        }

        const updatedDateRanges = [...dateRangeArray, newDateRange]
        handleChange(updatedDateRanges)
    }

    const removeDateRange = (index: number) => {
        const updatedDateRanges = dateRangeArray.filter((_, i) => i !== index)
        handleChange(updatedDateRanges)
    }

    const handleStartDateChange = (index: number, date: Date): void => {
        console.log('handleStartDateChange:', date)
        const formattedDate = format(date, 'yyyy-MM-dd')
        const thisDateRange = dateRangeArray[index]

        if (thisDateRange.end && formattedDate > thisDateRange.end) {
            toast.error('Start date cannot be after the end date')
            return
        }

        const updatedDateRanges = dateRangeArray.map((window, i) =>
            i === index ? { ...window, start: formattedDate } : window
        )

        // Validate all date ranges before updating
        for (let i = 0; i < updatedDateRanges.length; i++) {
            const error = validateDateRange(updatedDateRanges[i])
            if (error) {
                toast.error(`Date range ${i + 1}: ${error}`)
                return
            }
        }

        handleChange(updatedDateRanges)
    }

    const handleEndDateChange = (index: number, date: Date): void => {
        const formattedDate = format(date, 'yyyy-MM-dd')
        const currentWindow = dateRangeArray[index]

        if (currentWindow.start && formattedDate < currentWindow.start) {
            toast.error('End date cannot be before the start date')
            return
        }

        const updatedDateRanges = dateRangeArray.map((window, i) =>
            i === index ? { ...window, end: formattedDate } : window
        )

        // Validate all date ranges before updating
        for (let i = 0; i < updatedDateRanges.length; i++) {
            const error = validateDateRange(updatedDateRanges[i])
            if (error) {
                toast.error(`Date range ${i + 1}: ${error}`)
                return
            }
        }

        handleChange(updatedDateRanges)
    }

    return (
        <div className='form-group'>
            <div className='flex items-center justify-between mb-4'>
                <Label htmlFor={fieldName}>{label}</Label>
                <Button type='button' variant='outline' className='w-fit' onClick={addDateRange}>
                    Add Date <FaPlus className='w-5 h-5 ml-2' />
                </Button>
            </div>
            {dateRangeArray.length > 0 ? (
                dateRangeArray.map((eachDate, index) => (
                    <Card className='p-4 mb-4' key={index}>
                        <div
                            onClick={() => removeDateRange(index)}
                            className='flex mb-4 place-content-end w-full cursor-pointer'
                        >
                            <LiaTimesSolid className='w-5 h-5' />
                        </div>
                        <div className='flex flex-col gap-2 mx-auto'>
                            <Label htmlFor='start_date'>Start Date</Label>
                            <DatePicker
                                label='Start Date'
                                date={eachDate.start ? new Date(eachDate.start) : undefined}
                                setDate={date => date && handleStartDateChange(index, date)}
                            />
                            <Label htmlFor='end_date'>End Date</Label>
                            <DatePicker
                                label='End Date'
                                date={eachDate.end ? new Date(eachDate.end) : undefined}
                                setDate={date => date && handleEndDateChange(index, date)}
                            />
                        </div>
                    </Card>
                ))
            ) : (
                <Card className='py-8 px-4'>
                    <div className='w-fit mx-auto mb-3'>
                        <CalendarX className='h-8 w-8' />
                    </div>
                    <div className='text-center'>No dates</div>
                </Card>
            )}
        </div>
    )
}

export default DateRangeArrayPicker
